import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect } from 'react'
import { getLoadsByTagAndLoad } from '../services/loadService'
import type { LoadStates } from '../types/storageTypes'
import { timestampUtils } from '../utils/Timestamp'
import OpenMap from './OpenMaps'
import TimePicker from './Timepicker'

interface DropoffLoadSectionProps {
  loadStates: LoadStates
  setLoadStates: React.Dispatch<React.SetStateAction<LoadStates>>
  tagId?: string
  loadNumber?: string
  isFinal?: boolean
  dropoffLocation: string
}

const DropoffLoadSection: React.FC<DropoffLoadSectionProps> = ({
  loadStates,
  setLoadStates,
  tagId,
  loadNumber,
  isFinal = false,
  dropoffLocation,
}) => {
  useEffect(() => {
    const loadData = async () => {
      // First check localStorage
      const localUnloadArriving = localStorage.getItem(
        `time_unloadArriving_${tagId}_${loadNumber}`,
      )
      const localUnloadDeparting = localStorage.getItem(
        `time_unloadDeparting_${tagId}_${loadNumber}`,
      )

      // If we have any data in localStorage, use it and don't fetch
      if (localUnloadArriving || localUnloadDeparting) {
        setLoadStates((prev) => ({
          ...prev,
          unloadArriving: {
            timestamp: localUnloadArriving || prev.unloadArriving.timestamp,
            isActive: !!localUnloadArriving,
          },
          unloadDeparting: {
            timestamp: localUnloadDeparting || prev.unloadDeparting.timestamp,
            isActive: !!localUnloadDeparting,
          },
        }))
        return
      }

      // If no localStorage data, fetch from API
      if (tagId && loadNumber) {
        try {
          const loadData = await getLoadsByTagAndLoad(tagId, loadNumber)
          if (loadData) {
            setLoadStates((prev) => ({
              ...prev,
              unloadArriving: {
                timestamp: timestampUtils.formatTimestamp(
                  loadData.unload_arrival_time,
                ),
                isActive: !!loadData.unload_arrival_time,
              },
              unloadDeparting: {
                timestamp: timestampUtils.formatTimestamp(
                  loadData.unload_departure_time,
                ),
                isActive: !!loadData.unload_departure_time,
              },
            }))
          }
        } catch (error) {
          console.error('Failed to load data:', error)
        }
      }
    }

    loadData()
  }, [tagId, loadNumber, setLoadStates])

  const timeKeys = ['unloadArriving', 'unloadDeparting'] as const
  const labels = {
    unloadArriving: 'Unloading Arrive Time',
    unloadDeparting: 'Unloading Depart Time',
  } as const

  const renderTimeInput = (key: 'unloadArriving' | 'unloadDeparting') => {
    return (
      <TimePicker
        key={key}
        value={loadStates[key].timestamp}
        defaultTime={(() => {
          if (loadStates[key].timestamp) return undefined
          const now = new Date()
          let hours = now.getHours()
          const minutes = now.getMinutes()
          const period = hours >= 12 ? 'PM' : 'AM'
          hours = hours % 12 || 12
          const mm = minutes.toString().padStart(2, '0')
          return `${hours}:${mm} ${period}`
        })()}
        onChange={(newTime) => {
          if (isFinal) return

          localStorage.setItem(`time_${key}_${tagId}_${loadNumber}`, newTime)
          setLoadStates((prev) => ({
            ...prev,
            [key]: {
              timestamp: newTime,
              isActive: true,
            },
          }))
        }}
        label={labels[key]}
        buttonText={`Set ${labels[key]}`}
        disabled={isFinal}
      />
    )
  }

  return (
    <div className={css(styles.formContainer)}>
      <div className={css(styles.locationContainer)}>
        <h3 className={css(styles.locationLabel)}>DROP OFF LOCATION</h3>
        <OpenMap
          address={dropoffLocation}
          className={css(styles.locationText)}
        />
      </div>
      <div className={css(styles.loadButtons)}>
        {timeKeys.map(renderTimeInput)}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  formContainer: {
    width: '90%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  locationContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  locationLabel: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '125%',
    color: '#6B7280',
    margin: 0,
  },
  locationText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1C64F2',
    margin: 0,
    marginBottom: '4px',
    marginTop: '4px',
  },
  loadButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
  },
})

export default DropoffLoadSection
